"use client";

import Link from "next/link";
import { capture } from "@/lib/analytics/events";
import { ClaimForm } from "@/components/OrgReviewForms";
import type { FormResult } from "@/lib/org-reviews";

/**
 * "Work at X?" CTA on org pages, aimed at the org's own staff landing here
 * (search, a shared link, a candidate forwarding it). Two routes: claim the
 * page (respond to reviews, keep the profile accurate) or /post-a-job, the
 * real checkout entry point.
 *
 * Keep this component in sync with internationaljobs/src/components/
 * OrgClaimBanner.tsx: the two boards are one product.
 */
export function OrgClaimBanner({
  orgKey,
  label,
  claimAction,
}: {
  orgKey: string;
  label: string;
  /** Org page only. Without it (job page sidebar) this is the plain post-a-job CTA. */
  claimAction?: (fd: FormData) => Promise<FormResult>;
}) {
  const postLink = (text: string) => (
    <Link
      href="/post-a-job"
      onClick={() => capture("org_claim_cta_click", { org_key: orgKey })}
      className="font-semibold text-brand-700 underline-offset-2 hover:underline"
    >
      {text}
    </Link>
  );
  return (
    <div className="mt-6 rounded-lg border border-brand-100 bg-brand-50/50 px-4 py-3">
      {claimAction ? (
        <>
          <p className="text-[13px] leading-relaxed text-neutral-700">
            <span className="font-semibold text-ink">Work at {label}?</span>{" "}
            Claim this page to respond to reviews and keep the profile accurate, or{" "}
            {postLink("post a job")} so candidates browsing this page see it first.
          </p>
          <div className="mt-1 text-[13px]">
            <ClaimForm orgLabel={label} action={claimAction} />
          </div>
        </>
      ) : (
        <p className="text-[13px] leading-relaxed text-neutral-700">
          <span className="font-semibold text-ink">Hiring at {label}?</span>{" "}
          Post your team&rsquo;s open roles here so finance candidates already
          browsing this page see them first.{" "}
          {postLink("Post a job →")}
        </p>
      )}
    </div>
  );
}
